import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import { useState, useEffect } from "react";
import { getConfigWithToken } from "../config/config";

const TEAL = "#27B99A";
const TEAL_DARK = "#0F766E";
const LABEL = "#374151";
const MUTED = "#6B7280";
const BORDER = "#E5E7EB";
const ERROR = "#DC2626";

const licenseTypeOptions = [
  "Certified SFBT Coach",
  "LCSW – Licensed Clinical Social Worker",
  "LMSW – Licensed Master Social Worker",
  "LSW – Licensed Social Worker",
  "LICSW – Licensed Independent Clinical Social Worker",
  "LCSW-C – Licensed Certified Social Worker–Clinical",
  "LGSW – Licensed Graduate Social Worker",
  "LPC – Licensed Professional Counselor",
  "LPCC – Licensed Professional Clinical Counselor",
  "LCPC – Licensed Clinical Professional Counselor",
  "LMHC – Licensed Mental Health Counselor",
  "LPC-A – Licensed Professional Counselor Associate",
  "LPCA – Licensed Professional Counselor Associate",
  "LMFT – Licensed Marriage and Family Therapist",
  "LAMFT – Licensed Associate Marriage and Family Therapist",
  "MFT-A – Marriage and Family Therapist Associate",
  "PhD – Licensed Psychologist",
  "PsyD – Licensed Psychologist",
  "MD – Psychiatrist",
  "PMHNP – Psychiatric Mental Health Nurse Practitioner",
  "APRN – Advanced Practice Registered Nurse",
];

const PROVIDER_TYPES = [
  "Therapist",
  "Care Manager",
  "Psychiatric Consultant",
];

const emptyLicense = () => ({
  state: "",
  credentials: "",
  licenseNumber: "",
  licenseExpiration: "",
});

const emptyForm = () => ({
  email: "",
  firstname: "",
  lastname: "",
  providerType: "",
  npiNumber: "",
  credentials: [emptyLicense()],
});

const RequiredMark = () => (
  <span style={{ color: ERROR, marginLeft: 2 }}>*</span>
);

const FieldLabel = ({ children, required }) => (
  <label
    style={{
      display: "block",
      fontSize: "13px",
      fontWeight: 500,
      color: LABEL,
      marginBottom: "6px",
      fontFamily: "Poppins, sans-serif",
    }}
  >
    {children}
    {required ? <RequiredMark /> : null}
  </label>
);

const fieldInputStyle = (hasError) => ({
  width: "100%",
  padding: "10px 12px",
  fontSize: "13px",
  border: `1px solid ${hasError ? ERROR : BORDER}`,
  borderRadius: "8px",
  backgroundColor: "#fff",
  color: "#111827",
  outline: "none",
  fontFamily: "Poppins, sans-serif",
  boxSizing: "border-box",
});

const AddProvider = ({ open = false, onClose, onSuccess }) => {
  const user = useSelector((state) => state?.user);
  const [formData, setFormData] = useState(emptyForm);
  const [fetchedStates, setFetchedStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/states/all`,
        );
        if (response.data.success) {
          const sorted = [...(response.data.data || [])].sort((a, b) =>
            (a.name || "").localeCompare(b.name || ""),
          );
          setFetchedStates(sorted);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (open) {
      setFormData(emptyForm());
      setErrors({});
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const { email, firstname, lastname, credentials, providerType, npiNumber } =
    formData;
  const isTherapist = providerType === "Therapist";

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleCredentialChange = (index, field, value) => {
    if (field === "licenseExpiration" && value) {
      const year = value.split("-")[0];
      if (year.length > 4) return;
    }
    setFormData((prev) => {
      const updated = [...prev.credentials];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, credentials: updated };
    });
  };

  const handleAddLicense = () => {
    setFormData((prev) => ({
      ...prev,
      credentials: [...prev.credentials, emptyLicense()],
    }));
  };

  const handleRemoveLicense = (index) => {
    if (formData.credentials.length <= 1) {
      toast.warning("You must have at least one license.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      credentials: prev.credentials.filter((_, i) => i !== index),
    }));
  };

  const validateFields = () => {
    const fieldErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!firstname.trim()) fieldErrors.firstname = "Required";
    if (!lastname.trim()) fieldErrors.lastname = "Required";
    if (!email.trim()) {
      fieldErrors.email = "Required";
    } else if (!emailRegex.test(email.trim())) {
      fieldErrors.email = "Enter a valid email";
    }
    if (!providerType) fieldErrors.providerType = "Required";

    if (providerType === "Therapist") {
      const npi = String(npiNumber || "").replace(/\D/g, "");
      if (!npi) {
        fieldErrors.npiNumber = "Required";
      } else if (npi.length !== 10) {
        fieldErrors.npiNumber = "NPI must be 10 digits";
      }

      const today = new Date().toISOString().split("T")[0];
      credentials.forEach((c, index) => {
        if (!c.state) fieldErrors[`license_${index}_state`] = "Required";
        if (!c.licenseNumber?.trim()) {
          fieldErrors[`license_${index}_licenseNumber`] = "Required";
        }
        if (!c.credentials) {
          fieldErrors[`license_${index}_credentials`] = "Required";
        }
        if (!c.licenseExpiration) {
          fieldErrors[`license_${index}_licenseExpiration`] = "Required";
        } else if (c.licenseExpiration < today) {
          fieldErrors[`license_${index}_licenseExpiration`] = "Cannot be past";
        }
      });
    }

    return fieldErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fieldErrors = validateFields();
    setErrors(fieldErrors);

    if (Object.keys(fieldErrors).length > 0) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    const businessId = user.user?.businessId;
    try {
      setSubmitting(true);
      const config = getConfigWithToken(
        user.user?.token || localStorage.getItem("user_id"),
      );

      let assignedRole = "provider";
      if (providerType === "Care Manager") {
        assignedRole = "careManager";
      } else if (providerType === "Psychiatric Consultant") {
        assignedRole = "psychiatricConsultant";
      }

      const payload = {
        email: email.trim(),
        firstName: firstname.trim(),
        lastName: lastname.trim(),
        providerType,
        role: assignedRole,
        credentials: isTherapist ? credentials : [],
      };

      if (isTherapist) {
        payload.npiNumber = String(npiNumber).replace(/\D/g, "");
      }

      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/create?businessId=${businessId}`,
        payload,
        config,
      );

      if (request.data.success) {
        toast.success(request.data.message || "Invitation sent successfully.");
        onSuccess?.(request.data.data);
      } else {
        toast.error(request.data.message || "Could not create provider.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Could not create provider.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="add-provider-drawer-overlay" onClick={onClose}>
      <div
        className="add-provider-drawer-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-provider-title"
      >
        <div className="add-provider-drawer-header">
          <h2 id="add-provider-title">Add Provider</h2>
          <p>Enter provider information to get started.</p>
        </div>

        <form className="add-provider-drawer-form" onSubmit={handleSubmit}>
          <div className="add-provider-drawer-body">
            <div className="add-provider-row-2">
              <div>
                <FieldLabel required>First Name</FieldLabel>
                <input
                  name="firstname"
                  value={firstname}
                  type="text"
                  placeholder="First name"
                  onChange={onChange}
                  style={fieldInputStyle(!!errors.firstname)}
                />
                {errors.firstname && (
                  <div className="add-provider-error">{errors.firstname}</div>
                )}
              </div>
              <div>
                <FieldLabel required>Last Name</FieldLabel>
                <input
                  name="lastname"
                  value={lastname}
                  type="text"
                  placeholder="Last name"
                  onChange={onChange}
                  style={fieldInputStyle(!!errors.lastname)}
                />
                {errors.lastname && (
                  <div className="add-provider-error">{errors.lastname}</div>
                )}
              </div>
            </div>

            <div className="add-provider-field">
              <FieldLabel required>Email</FieldLabel>
              <input
                name="email"
                value={email}
                type="email"
                placeholder="email@example.com"
                onChange={onChange}
                style={fieldInputStyle(!!errors.email)}
              />
              {errors.email && (
                <div className="add-provider-error">{errors.email}</div>
              )}
            </div>

            <div className="add-provider-field">
              <FieldLabel required>Provider Type</FieldLabel>
              <select
                name="providerType"
                value={providerType}
                onChange={onChange}
                style={fieldInputStyle(!!errors.providerType)}
              >
                <option value="">Select type...</option>
                {PROVIDER_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.providerType && (
                <div className="add-provider-error">{errors.providerType}</div>
              )}
            </div>

            {isTherapist && (
              <>
                <div className="add-provider-field">
                  <FieldLabel required>NPI Number</FieldLabel>
                  <input
                    name="npiNumber"
                    value={npiNumber}
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Enter 10 digit NPI"
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setFormData((prev) => ({ ...prev, npiNumber: digits }));
                      if (errors.npiNumber) {
                        setErrors((prev) => ({ ...prev, npiNumber: null }));
                      }
                    }}
                    style={fieldInputStyle(!!errors.npiNumber)}
                  />
                  {errors.npiNumber && (
                    <div className="add-provider-error">{errors.npiNumber}</div>
                  )}
                </div>

                {credentials.map((license, index) => (
                  <div key={index} className="add-provider-license-card">
                    <div className="add-provider-license-card-header">
                      <h3>License {index + 1}</h3>
                      {credentials.length > 1 && (
                        <button
                          type="button"
                          className="add-provider-remove-link"
                          onClick={() => handleRemoveLicense(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="add-provider-field">
                      <FieldLabel required>State</FieldLabel>
                      <select
                        value={license.state}
                        onChange={(e) =>
                          handleCredentialChange(index, "state", e.target.value)
                        }
                        style={fieldInputStyle(
                          !!errors[`license_${index}_state`],
                        )}
                      >
                        <option value="">Select state...</option>
                        {fetchedStates.map((opt) => (
                          <option key={opt._id} value={opt.name}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                      {errors[`license_${index}_state`] && (
                        <div className="add-provider-error">
                          {errors[`license_${index}_state`]}
                        </div>
                      )}
                    </div>

                    <div className="add-provider-field">
                      <FieldLabel required>License Number</FieldLabel>
                      <input
                        type="text"
                        placeholder="e.g. LPC-23451"
                        value={license.licenseNumber}
                        onChange={(e) =>
                          handleCredentialChange(
                            index,
                            "licenseNumber",
                            e.target.value,
                          )
                        }
                        style={fieldInputStyle(
                          !!errors[`license_${index}_licenseNumber`],
                        )}
                      />
                      {errors[`license_${index}_licenseNumber`] && (
                        <div className="add-provider-error">
                          {errors[`license_${index}_licenseNumber`]}
                        </div>
                      )}
                    </div>

                    <div className="add-provider-field">
                      <FieldLabel required>License Type</FieldLabel>
                      <select
                        value={license.credentials}
                        onChange={(e) =>
                          handleCredentialChange(
                            index,
                            "credentials",
                            e.target.value,
                          )
                        }
                        style={fieldInputStyle(
                          !!errors[`license_${index}_credentials`],
                        )}
                      >
                        <option value="">Select license type...</option>
                        {licenseTypeOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      {errors[`license_${index}_credentials`] && (
                        <div className="add-provider-error">
                          {errors[`license_${index}_credentials`]}
                        </div>
                      )}
                    </div>

                    <div className="add-provider-field" style={{ marginBottom: 0 }}>
                      <FieldLabel required>Expiration Date</FieldLabel>
                      <input
                        type="date"
                        min={new Date().toISOString().split("T")[0]}
                        placeholder="MM/DD/YYYY"
                        value={license.licenseExpiration}
                        onChange={(e) =>
                          handleCredentialChange(
                            index,
                            "licenseExpiration",
                            e.target.value,
                          )
                        }
                        style={fieldInputStyle(
                          !!errors[`license_${index}_licenseExpiration`],
                        )}
                      />
                      {errors[`license_${index}_licenseExpiration`] && (
                        <div className="add-provider-error">
                          {errors[`license_${index}_licenseExpiration`]}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="add-provider-add-license"
                  onClick={handleAddLicense}
                >
                  + Add Another License
                </button>
              </>
            )}
          </div>

          <div className="add-provider-drawer-footer">
            <button
              type="button"
              className="add-provider-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="add-provider-submit"
              disabled={submitting}
            >
              {submitting ? "Sending..." : "Send Invite"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .add-provider-drawer-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          display: flex;
          justify-content: flex-end;
          z-index: 10050;
        }
        .add-provider-drawer-panel {
          width: 480px;
          max-width: 100vw;
          height: 100vh;
          background: #ffffff;
          box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
          display: flex;
          flex-direction: column;
          font-family: Poppins, sans-serif;
        }
        .add-provider-drawer-header {
          padding: 24px 24px 16px;
          border-bottom: 1px solid ${BORDER};
          flex-shrink: 0;
        }
        .add-provider-drawer-header h2 {
          margin: 0 0 6px;
          font-size: 22px;
          font-weight: 700;
          color: #064260;
          line-height: 1.2;
        }
        .add-provider-drawer-header p {
          margin: 0;
          font-size: 13px;
          color: ${MUTED};
          line-height: 1.4;
        }
        .add-provider-drawer-form {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }
        .add-provider-drawer-body {
          flex: 1;
          overflow-y: auto;
          padding: 20px 24px 24px;
        }
        .add-provider-row-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 16px;
        }
        .add-provider-field {
          margin-bottom: 16px;
        }
        .add-provider-error {
          margin-top: 4px;
          font-size: 12px;
          color: ${ERROR};
        }
        .add-provider-license-card {
          border: 1px solid ${BORDER};
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 14px;
          background: #fff;
        }
        .add-provider-license-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .add-provider-license-card-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #111827;
        }
        .add-provider-remove-link {
          border: none;
          background: none;
          color: ${ERROR};
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          padding: 0;
          font-family: Poppins, sans-serif;
        }
        .add-provider-add-license {
          border: none;
          background: none;
          color: ${TEAL};
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          padding: 4px 0;
          font-family: Poppins, sans-serif;
        }
        .add-provider-add-license:hover {
          color: ${TEAL_DARK};
        }
        .add-provider-drawer-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid ${BORDER};
          background: #fff;
          flex-shrink: 0;
        }
        .add-provider-cancel {
          border: none;
          background: none;
          color: ${MUTED};
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          padding: 8px 4px;
          font-family: Poppins, sans-serif;
        }
        .add-provider-cancel:hover {
          color: #374151;
        }
        .add-provider-submit {
          border: none;
          background: ${TEAL};
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          padding: 10px 22px;
          border-radius: 8px;
          font-family: Poppins, sans-serif;
          min-width: 120px;
        }
        .add-provider-submit:hover {
          background: #22a388;
        }
        .add-provider-submit:disabled,
        .add-provider-cancel:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        @media (max-width: 560px) {
          .add-provider-drawer-panel {
            width: 100vw;
          }
          .add-provider-row-2 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AddProvider;
